import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { APIStatus } from '../../../enum';
import { removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

export type AdminAdvocateHours = {
  id: string;
  advocate_name: string;
  first_name: string;
  last_name: string;
  months_online: string[];
  hours_online: number[];
  account_status: string;
  total_count: number;
  org_name: string;
  org_timezone_name: string | null;
  is_service_provider: boolean;
};

type AdminAdvocatesHoursSliceType = {
  adminAdvocateHoursApiStatus: APIStatus;
  adminAdvocateHoursData: AdminAdvocateHours[];
  requestAdminAdvocateHoursParam: RequestParams<AdminAdvocateHours>;
  adminAdvocateHoursDataForCSV: AdminAdvocateHours[];
  adminAdvocateHoursForCSVApiStatus: APIStatus;
  adminAdvocateHourMonthColumns: string[];
};

const initialState: AdminAdvocatesHoursSliceType = {
  adminAdvocateHoursData: [],
  requestAdminAdvocateHoursParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<AdminAdvocateHours>('advocate_name', 'ascend'),
    filteredInfo: {},
  },
  adminAdvocateHoursApiStatus: APIStatus.IDLE,
  adminAdvocateHoursDataForCSV: [],
  adminAdvocateHoursForCSVApiStatus: APIStatus.IDLE,
  adminAdvocateHourMonthColumns: [],
};

export const fetchAdminAdvocateHours = createAsyncThunk<AdminAdvocateHours[], undefined, AsyncThunkConfig>(
  'adminAdvocateHours/fetchAdminAdvocateHours',
  async (_, thunkAPI) => {
    try {
      const requestAdminAdvocateHoursParam = thunkAPI.getState().adminAdvocateHoursSlice.requestAdminAdvocateHoursParam;
      const response = await axios.post('v0_get_advocate_hours', requestAdminAdvocateHoursParam);
      return response as unknown as AdminAdvocateHours[];
    } catch (e) {
      logError(e, ['adminAdvocateHoursSlice', 'fetchAdminAdvocateHours']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchAdminAdvocateHoursForCSV = createAsyncThunk<AdminAdvocateHours[], undefined, AsyncThunkConfig>(
  'adminAdvocateHours/fetchAdminAdvocateHoursForCSV',
  async (_, thunkAPI) => {
    try {
      const response = await axios.post('v0_get_advocate_hours', {
        sortedInfo: new SortedInfo<AdminAdvocateHours>('advocate_name', 'ascend'),
      });
      return response as unknown as AdminAdvocateHours[];
    } catch (e) {
      logError(e, ['adminAdvocateHoursSlice', 'fetchAdminAdvocateHoursForCSV']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const adminAdvocateHoursSlice = createSlice({
  name: 'adminAdvocateHours',
  initialState,
  reducers: {
    handleAdvocateHoursTableChange: (state, action: PayloadAction<TableChange<AdminAdvocateHours>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<AdminAdvocateHours>;
      state.requestAdminAdvocateHoursParam.pagination = action.payload.pagination;
      state.requestAdminAdvocateHoursParam.sortedInfo = sorter;
      state.requestAdminAdvocateHoursParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestAdminAdvocateHoursParam.pagination = initialState.requestAdminAdvocateHoursParam.pagination;
      state.requestAdminAdvocateHoursParam.sortedInfo = initialState.requestAdminAdvocateHoursParam.sortedInfo;
      state.requestAdminAdvocateHoursParam.filteredInfo = initialState.requestAdminAdvocateHoursParam.filteredInfo;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminAdvocateHours.pending, (state, _action) => {
        state.adminAdvocateHoursApiStatus = APIStatus.PENDING;
      })
      .addCase(fetchAdminAdvocateHours.fulfilled, (state, action) => {
        state.adminAdvocateHoursData = action.payload;
        if (action.payload.length > 0) {
          state.adminAdvocateHourMonthColumns = action.payload[0].months_online;
        }
        state.adminAdvocateHoursApiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchAdminAdvocateHours.rejected, (state, _action) => {
        state.adminAdvocateHoursApiStatus = APIStatus.ERROR;
      })
      .addCase(fetchAdminAdvocateHoursForCSV.pending, (state, _action) => {
        state.adminAdvocateHoursForCSVApiStatus = APIStatus.PENDING;
      })
      .addCase(fetchAdminAdvocateHoursForCSV.fulfilled, (state, action) => {
        state.adminAdvocateHoursDataForCSV = action.payload;
        state.adminAdvocateHoursForCSVApiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchAdminAdvocateHoursForCSV.rejected, (state, _action) => {
        state.adminAdvocateHoursForCSVApiStatus = APIStatus.ERROR;
      });
  },
});

export const { handleAdvocateHoursTableChange, clearFilter } = adminAdvocateHoursSlice.actions;
